/**
 * FhirSubscriptionsDashboard.js — P3: Subscription Health Monitoring.
 *
 * Surfaces every row from `/api/fhir/subscriptions/registrations` with
 * status/health badges, sync metadata, and per-row actions (activate /
 * deactivate / retry / delete). Supports status + topic filters, manual
 * and auto-refresh, and a detail drawer showing the full registration
 * config + last error + sync history.
 *
 * Same-origin wiring throughout (enforced by httpClient's
 * SAME_ORIGIN_ONLY_PATHS lock for `/api/fhir/subscriptions/*`).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RefreshCw, Activity, PauseCircle, PlayCircle, RotateCw, Trash2,
  AlertTriangle, Loader2, CheckCircle2, XCircle, Clock, Bell, Search,
  X, Database,
} from 'lucide-react';
import { toast } from 'sonner';
import * as fhirSubsApi from '../lib/api/fhirSubscriptionsApi';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'requested', label: 'Requested' },
  { value: 'active', label: 'Active' },
  { value: 'error', label: 'Error' },
  { value: 'off', label: 'Off' },
  { value: 'entered-in-error', label: 'Entered-in-error' },
];

const REFRESH_INTERVAL_MS = 20000;

// ─── Pure badge-mapping helpers (also exercised by tests) ─────────────
export const statusBadgeClass = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'active':            return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    case 'requested':         return 'bg-sky-100 text-sky-800 border-sky-300';
    case 'error':             return 'bg-rose-100 text-rose-800 border-rose-300';
    case 'off':               return 'bg-slate-200 text-slate-700 border-slate-300';
    case 'entered-in-error':  return 'bg-orange-100 text-orange-800 border-orange-300';
    default:                  return 'bg-slate-100 text-slate-600 border-slate-300';
  }
};

export const syncBadgeClass = (syncStatus) => {
  switch ((syncStatus || '').toLowerCase()) {
    case 'ok':          return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'pending':     return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'unavailable': return 'bg-orange-50 text-orange-800 border-orange-200';
    case 'error':       return 'bg-rose-50 text-rose-700 border-rose-200';
    default:            return 'bg-slate-50 text-slate-600 border-slate-200';
  }
};

export const formatTs = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const Badge = ({ children, className = '', testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full border ${className}`}
  >
    {children}
  </span>
);

// ─── Row component ────────────────────────────────────────────────────
function RegistrationRow({ reg, busy, onAction, onSelect }) {
  const isBusy = busy === reg.id;
  return (
    <tr
      data-testid={`sub-row-${reg.id}`}
      className="border-b border-slate-200 hover:bg-slate-50 transition-colors"
    >
      <td className="px-4 py-3">
        <button
          data-testid={`sub-row-open-${reg.id}`}
          onClick={() => onSelect(reg)}
          className="text-left group"
        >
          <div className="font-semibold text-slate-900 group-hover:text-indigo-600">
            {reg.name || <em className="text-slate-400">(unnamed)</em>}
          </div>
          <div className="text-xs text-slate-500 truncate max-w-sm" title={reg.topic}>
            {reg.topic}
          </div>
        </button>
      </td>
      <td className="px-4 py-3">
        <Badge testid={`sub-status-${reg.id}`} className={statusBadgeClass(reg.status)}>
          {reg.status}
        </Badge>
      </td>
      <td className="px-4 py-3">
        <Badge testid={`sub-sync-${reg.id}`} className={syncBadgeClass(reg.last_sync_status)}>
          {reg.last_sync_status || 'unknown'}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm text-slate-700" data-testid={`sub-retry-count-${reg.id}`}>
        {reg.retry_count || 0}
      </td>
      <td className="px-4 py-3 text-sm text-slate-700" data-testid={`sub-notif-count-${reg.id}`}>
        {reg.notification_count || 0}
      </td>
      <td className="px-4 py-3 text-xs text-slate-600" data-testid={`sub-last-notif-${reg.id}`}>
        {formatTs(reg.last_notification_at)}
      </td>
      <td className="px-4 py-3 text-xs text-slate-600" data-testid={`sub-updated-${reg.id}`}>
        {formatTs(reg.updated_at)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          {reg.status !== 'active' && (
            <button
              data-testid={`sub-activate-${reg.id}`}
              disabled={isBusy}
              onClick={() => onAction(reg, 'activate')}
              title="Activate"
              className="p-1.5 rounded hover:bg-emerald-100 text-emerald-700 disabled:opacity-50"
            >
              <PlayCircle className="w-4 h-4" />
            </button>
          )}
          {reg.status === 'active' && (
            <button
              data-testid={`sub-deactivate-${reg.id}`}
              disabled={isBusy}
              onClick={() => onAction(reg, 'deactivate')}
              title="Deactivate"
              className="p-1.5 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-50"
            >
              <PauseCircle className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid={`sub-retry-${reg.id}`}
            disabled={isBusy}
            onClick={() => onAction(reg, 'retry')}
            title="Retry remote sync"
            className="p-1.5 rounded hover:bg-indigo-100 text-indigo-700 disabled:opacity-50"
          >
            <RotateCw className={`w-4 h-4 ${isBusy ? 'animate-spin' : ''}`} />
          </button>
          <button
            data-testid={`sub-delete-${reg.id}`}
            disabled={isBusy}
            onClick={() => onAction(reg, 'delete')}
            title="Delete"
            className="p-1.5 rounded hover:bg-rose-100 text-rose-700 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Detail drawer ─────────────────────────────────────────────────────
function DetailDrawer({ registration, onClose }) {
  if (!registration) return null;
  const r = registration;
  const Row = ({ label, value, testid }) => (
    <div className="flex flex-col gap-0.5 py-2 border-b border-slate-100 last:border-b-0">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <span className="text-sm text-slate-900 break-all" data-testid={testid}>
        {value ?? <em className="text-slate-400">—</em>}
      </span>
    </div>
  );
  return (
    <div
      data-testid="sub-detail-drawer"
      className="fixed inset-0 z-[60] flex justify-end"
      role="dialog"
      aria-modal="true"
    >
      <button
        className="absolute inset-0 bg-slate-900/30"
        onClick={onClose}
        aria-label="Close"
        data-testid="sub-detail-backdrop"
      />
      <div className="relative w-full max-w-md bg-white h-full shadow-xl overflow-y-auto p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{r.name}</h2>
            <p className="text-xs text-slate-500 mt-0.5">ID: {r.id}</p>
          </div>
          <button
            data-testid="sub-detail-close"
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <Badge className={statusBadgeClass(r.status)}>{r.status}</Badge>
          <Badge className={syncBadgeClass(r.last_sync_status)}>{r.last_sync_status || 'unknown'}</Badge>
        </div>

        <div className="space-y-0">
          <Row label="Topic" value={r.topic} testid="sub-detail-topic" />
          <Row label="Reason" value={r.reason} />
          <Row label="Channel" value={`${r.channel_type} · ${r.content_type}`} />
          <Row label="Endpoint" value={r.endpoint} testid="sub-detail-endpoint" />
          <Row label="Content" value={r.content} />
          <Row label="Org" value={r.org_id} />
          <Row label="Heartbeat" value={r.heartbeat_period ? `${r.heartbeat_period}s` : '—'} />
          <Row label="Timeout" value={r.timeout ? `${r.timeout}s` : '—'} />
          <Row label="Max count" value={r.max_count ?? '—'} />
          <Row label="FHIR server id" value={r.fhir_server_id || '—'} testid="sub-detail-remote-id" />
          <Row label="Last sync" value={formatTs(r.last_sync_at)} />
          <Row label="Last notification" value={formatTs(r.last_notification_at)} />
          <Row label="Notifications received" value={r.notification_count ?? 0} />
          <Row label="Retry count" value={r.retry_count ?? 0} testid="sub-detail-retry-count" />
          <Row label="End" value={formatTs(r.end)} />
          <Row label="Contact" value={r.contact} />
          <Row label="Managing entity" value={r.managing_entity} />
          <Row label="Created" value={formatTs(r.created_at)} />
          <Row label="Updated" value={formatTs(r.updated_at)} />
        </div>

        {r.last_error && (
          <div
            data-testid="sub-detail-error"
            className="mt-4 p-3 rounded bg-rose-50 border border-rose-200"
          >
            <div className="flex items-center gap-2 text-rose-800 font-semibold text-sm mb-1">
              <AlertTriangle className="w-4 h-4" />
              Last error
              <span className="text-xs text-rose-600 font-normal ml-auto">
                {formatTs(r.last_error_at)}
              </span>
            </div>
            <div className="text-xs text-rose-700 break-words">{r.last_error}</div>
          </div>
        )}

        {Array.isArray(r.filter_by) && r.filter_by.length > 0 && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">FilterBy</div>
            <ul className="space-y-1" data-testid="sub-detail-filter-by">
              {r.filter_by.map((f, i) => (
                <li key={i} className="text-xs bg-slate-50 border border-slate-200 rounded p-2 font-mono">
                  {f.resourceType}.{f.filterParameter} {f.comparator || 'eq'} {f.value}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Delete confirm modal ─────────────────────────────────────────────
function ConfirmDelete({ registration, busy, onCancel, onConfirm }) {
  if (!registration) return null;
  return (
    <div
      data-testid="sub-delete-confirm"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
        <div className="flex items-center gap-2 text-rose-700 mb-3">
          <AlertTriangle className="w-5 h-5" />
          <h3 className="text-lg font-semibold">Delete subscription?</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          This will remove <span className="font-semibold">{registration.name}</span> locally
          and attempt to delete it from the upstream FHIR server. This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <button
            data-testid="sub-delete-cancel"
            disabled={busy}
            onClick={onCancel}
            className="px-4 py-2 rounded bg-slate-100 hover:bg-slate-200 text-sm text-slate-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="sub-delete-confirm-btn"
            disabled={busy}
            onClick={onConfirm}
            className="px-4 py-2 rounded bg-rose-600 hover:bg-rose-700 text-sm text-white font-semibold disabled:opacity-50 flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────
export default function FhirSubscriptionsDashboard() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (topicFilter.trim()) params.topic = topicFilter.trim();
    const r = await fhirSubsApi.listRegistrations(params);
    if (r.ok) {
      setRegistrations(r.data?.registrations || []);
      setLastRefreshAt(new Date());
      setFeatureEnabled(true);
    } else {
      if (r.error?.status === 403) {
        setFeatureEnabled(false);
        setError('FHIR Subscriptions feature is disabled. Ask an admin to enable FHIR_SUBSCRIPTIONS_ENABLED.');
      } else {
        setError(r.error?.message || 'Failed to load subscriptions');
      }
      setRegistrations([]);
    }
    setLoading(false);
  }, [statusFilter, topicFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh timer
  useEffect(() => {
    if (!autoRefresh) return;
    timerRef.current = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoRefresh, refresh]);

  const onAction = async (reg, action) => {
    if (action === 'delete') { setPendingDelete(reg); return; }
    setBusy(reg.id);
    let res;
    if (action === 'activate')   res = await fhirSubsApi.activateRegistration(reg.id);
    if (action === 'deactivate') res = await fhirSubsApi.deactivateRegistration(reg.id);
    if (action === 'retry')      res = await fhirSubsApi.retryRegistration(reg.id);
    setBusy(null);

    if (res?.ok) {
      toast.success(`${action} succeeded`);
      setRegistrations((prev) => prev.map((x) => (x.id === reg.id ? { ...x, ...res.data } : x)));
      if (selected?.id === reg.id) setSelected({ ...selected, ...res.data });
    } else {
      toast.error(res?.error?.message || `${action} failed`);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setBusy(pendingDelete.id);
    const res = await fhirSubsApi.deleteRegistration(pendingDelete.id, { syncToRemote: true });
    setBusy(null);
    if (res?.ok) {
      toast.success('Deleted');
      setRegistrations((prev) => prev.filter((x) => x.id !== pendingDelete.id));
      if (selected?.id === pendingDelete.id) setSelected(null);
      setPendingDelete(null);
    } else {
      toast.error(res?.error?.message || 'Delete failed');
    }
  };

  const counts = useMemo(() => {
    const c = { total: registrations.length, active: 0, error: 0, off: 0 };
    for (const r of registrations) {
      if (r.status === 'active') c.active += 1;
      else if (r.status === 'error') c.error += 1;
      else if (r.status === 'off') c.off += 1;
    }
    return c;
  }, [registrations]);

  return (
    <div data-testid="fhir-subs-dashboard" className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Database className="w-6 h-6 text-indigo-600" />
            FHIR Subscription Health
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Monitor R5 topic-based subscriptions — activation, retry, and notification flow.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              data-testid="sub-auto-refresh-toggle"
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh (20s)
          </label>
          <button
            data-testid="sub-refresh-btn"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <CounterCard testid="sub-count-total"  icon={Database}     label="Total"  value={counts.total}  />
        <CounterCard testid="sub-count-active" icon={Activity}     label="Active" value={counts.active} tone="emerald" />
        <CounterCard testid="sub-count-error"  icon={XCircle}      label="Error"  value={counts.error}  tone="rose" />
        <CounterCard testid="sub-count-off"    icon={PauseCircle}  label="Off"    value={counts.off}    tone="slate" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-600">Status</span>
          <select
            data-testid="sub-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm flex-1 min-w-[240px]">
          <span className="text-slate-600">Topic</span>
          <div className="flex items-center gap-1 flex-1 border border-slate-300 rounded px-2 bg-white">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input
              data-testid="sub-filter-topic"
              value={topicFilter}
              onChange={(e) => setTopicFilter(e.target.value)}
              placeholder="canonical URL of a SubscriptionTopic"
              className="flex-1 py-1.5 text-sm outline-none"
            />
            {topicFilter && (
              <button
                data-testid="sub-filter-topic-clear"
                onClick={() => setTopicFilter('')}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </label>
        {lastRefreshAt && (
          <span className="text-xs text-slate-500" data-testid="sub-last-refresh-at">
            <Clock className="w-3 h-3 inline-block mr-1" />
            refreshed {lastRefreshAt.toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* States */}
      {error && (
        <div
          data-testid="sub-error-banner"
          className="p-3 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800 mb-4 flex items-start gap-2"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!featureEnabled && !error && (
        <div
          data-testid="sub-disabled-banner"
          className="p-4 rounded bg-sky-50 border border-sky-200 text-sm text-sky-800 mb-4"
        >
          FHIR Subscriptions is not enabled in this environment.
        </div>
      )}

      {loading && registrations.length === 0 && (
        <div
          data-testid="sub-loading"
          className="p-12 text-center text-slate-500 flex flex-col items-center gap-2"
        >
          <Loader2 className="w-6 h-6 animate-spin" />
          Loading subscriptions…
        </div>
      )}

      {!loading && !error && registrations.length === 0 && (
        <div
          data-testid="sub-empty"
          className="p-12 text-center text-slate-500 border border-dashed border-slate-300 rounded"
        >
          <Bell className="w-8 h-8 mx-auto mb-2 text-slate-400" />
          No subscription registrations yet.
          <div className="text-xs text-slate-400 mt-1">
            Use <code className="bg-slate-100 px-1 rounded">POST /api/fhir/subscriptions/registrations</code> to create one.
          </div>
        </div>
      )}

      {/* Table */}
      {registrations.length > 0 && (
        <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
          <table data-testid="sub-table" className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Subscription</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Last sync</th>
                <th className="px-4 py-3 text-left">Retry</th>
                <th className="px-4 py-3 text-left">Notifs</th>
                <th className="px-4 py-3 text-left">Last notif</th>
                <th className="px-4 py-3 text-left">Updated</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {registrations.map((r) => (
                <RegistrationRow
                  key={r.id}
                  reg={r}
                  busy={busy}
                  onAction={onAction}
                  onSelect={setSelected}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DetailDrawer registration={selected} onClose={() => setSelected(null)} />
      <ConfirmDelete
        registration={pendingDelete}
        busy={busy === pendingDelete?.id}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function CounterCard({ icon: Icon, label, value, tone = 'indigo', testid }) {
  const tones = {
    indigo:  'bg-indigo-50 border-indigo-200 text-indigo-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    rose:    'bg-rose-50 border-rose-200 text-rose-900',
    slate:   'bg-slate-50 border-slate-200 text-slate-900',
  };
  return (
    <div
      data-testid={testid}
      className={`p-4 rounded-lg border ${tones[tone]} flex items-center gap-3`}
    >
      <Icon className="w-6 h-6 opacity-80" />
      <div>
        <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
        <div className="text-2xl font-bold">{value}</div>
      </div>
    </div>
  );
}

// Re-export helpers for deterministic test assertions.
export { RegistrationRow, DetailDrawer, ConfirmDelete, CounterCard, REFRESH_INTERVAL_MS };
